// src/pages/EmployeeExpensePage.jsx
import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { BASE_URL } from "../../config";
import { getAuthHeaders } from "../../services/useAuth";
import CustomButton from "../../pages/CustomButton";
import ExpenseHistoryModal from "./ExpenseFf";

const EmployeeExpensePage = ({ empId, empName, month, year, onBack }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);

  // Format month name for API (e.g., "Sep" for "09")
  const formatMonthName = (monthNumber) => {
    const months = [
      "Jan", "Feb", "Mar", "Apr", "May", "Jun",
      "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
    ];
    return months[parseInt(monthNumber) - 1] || monthNumber;
  };

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        // Format month name for API (e.g., "Sep" for "09")
        const monthName = formatMonthName(month);
        
        // Call the employee daily expense report API
        const url = `${BASE_URL}/api/employee-daily-expense-report`;
        const params = {
          month_year: `${monthName} ${year}`,
          employee_id: empId
        };
        
        const res = await axios.get(url, { params, headers: getAuthHeaders() });
        const payload = Array.isArray(res.data) ? res.data : res.data?.data || [];
        
        // Map API response to table format
        const formattedRows = payload.map((item, index) => ({
          id: item.id || index,
          date: item.date || item.working_date || "-",
          route: item.route_detail || item.route || "-",
          type: item.type || item.expense_type || "-",
          distance: item.distance || item.km || 0,
          ta: item.ta || item.transport_allowance || 0,
          da: item.da || item.daily_allowance || 0,
          hotel: item.hotel || item.hotel_expense || 0,
          other: item.other || item.other_expenses || 0,
          emp_remark: item.emp_remark || item.employee_remarks || "-",
          manager_remark: item.manager_remark || item.manager_remarks || "-",
          status: item.status || "Pending",
          approved: item.approved || false
        }));
        
        setRows(formattedRows);
      } catch (e) {
        console.error("Error loading employee daily expenses", e);
        setRows([]);
      } finally {
        setLoading(false);
      }
    };
    
    if (empId) load();
  }, [empId, month, year]);

  const openHistory = (row) => {
    setSelectedRow(row);
    setHistoryOpen(true);
  };

  const handleApprove = async (row) => {
    try {
      // API call to approve the expense
      const url = `${BASE_URL}/api/expense/${row.id}/approve`;
      await axios.post(url, {}, { headers: getAuthHeaders() });
      
      // Update local state to reflect approval
      setRows(prevRows => 
        prevRows.map(r => 
          r.id === row.id ? { ...r, approved: true, status: "Approved" } : r
        )
      );
      
      alert("Expense approved successfully!");
    } catch (e) {
      console.error("Error approving expense", e);
      alert("Failed to approve expense");
    }
  };

  const handleExport = async () => {
    try {
      const monthName = formatMonthName(month);
      const url = `${BASE_URL}/api/employee-daily-expense-report/export`;
      const params = {
        month_year: `${monthName} ${year}`,
        employee_id: empId
      };
      
      const response = await axios.get(url, {
        params,
        headers: getAuthHeaders(),
        responseType: 'blob' // Important for file downloads
      });
      
      // Create a blob from the response
      const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      
      // Create a download link and trigger download
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.setAttribute('download', `${empName}_${monthName}_${year}_expenses.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      // Clean up the URL object
      window.URL.revokeObjectURL(downloadUrl);
    } catch (e) {
      console.error("Error exporting expenses", e);
      alert("Failed to export expenses");
    }
  };

  return (
    <div className="p-4">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h2 className="text-lg font-semibold">{empName} ({empId})</h2>
          <div className="text-sm text-gray-600">{formatMonthName(month)} {year}</div>
        </div>
        <div className="flex gap-2">
          <CustomButton onClick={onBack}>Back</CustomButton>
          <CustomButton onClick={handleExport}>Export To Excel</CustomButton>
        </div>
      </div>

      <div className="overflow-x-auto bg-white rounded shadow">
        <table className="min-w-max w-full text-sm table-fixed">
          <thead className="bg-amber-100">
            <tr>
              <th className="p-2 border w-12">S.No</th>
              <th className="p-2 border w-28">Date</th>
              <th className="p-2 border">Route</th>
              <th className="p-2 border">Type</th>
              <th className="p-2 border">Distance</th>
              <th className="p-2 border">TA</th>
              <th className="p-2 border">DA</th>
              <th className="p-2 border">Hotel</th>
              <th className="p-2 border">Other</th>
              <th className="p-2 border">Total</th>
              <th className="p-2 border">Employee Remarks</th>
              <th className="p-2 border">Manager Remarks</th>
              <th className="p-2 border">Status</th>
              <th className="p-2 border">Approve</th>
              <th className="p-2 border">History</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={15} className="p-6 text-center">Loading...</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={15} className="p-6 text-center">No expense data found for this period</td></tr>
            ) : rows.map((r, i) => {
              const total = (parseFloat(r.ta) || 0) + (parseFloat(r.da) || 0) + 
                           (parseFloat(r.hotel) || 0) + (parseFloat(r.other) || 0);
              
              return (
                <tr key={r.id} className={i % 2 === 0 ? "" : "bg-green-50"}>
                  <td className="p-2 border text-center">{i+1}</td>
                  <td className="p-2 border">{r.date}</td>
                  <td className="p-2 border">{r.route}</td>
                  <td className="p-2 border">{r.type}</td>
                  <td className="p-2 border text-right">{r.distance}</td>
                  <td className="p-2 border text-right">₹{r.ta}</td>
                  <td className="p-2 border text-right">₹{r.da}</td>
                  <td className="p-2 border text-right">₹{r.hotel}</td>
                  <td className="p-2 border text-right">₹{r.other}</td>
                  <td className="p-2 border text-right font-medium">₹{total}</td>
                  <td className="p-2 border">{r.emp_remark}</td>
                  <td className="p-2 border">{r.manager_remark}</td>
                  <td className="p-2 border text-center">
                    <span className={`px-2 py-1 rounded text-xs ${
                      r.approved ? "bg-green-100 text-green-800" : "bg-yellow-100 text-yellow-800"
                    }`}>
                      {r.approved ? "Approved" : "Pending"}
                    </span>
                  </td>
                  <td className="p-2 border text-center">
                    <CustomButton 
                      onClick={() => handleApprove(r)} 
                      disabled={r.approved}
                      className={r.approved ? "bg-gray-300" : ""}
                    >
                      {r.approved ? "Approved" : "Approve"}
                    </CustomButton>
                  </td>
                  <td className="p-2 border text-center">
                    <CustomButton onClick={() => openHistory(r)}>View</CustomButton>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <ExpenseHistoryModal
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        row={selectedRow}
        empId={empId}
      />
    </div>
  );
};

export default EmployeeExpensePage;